













































































































































import { ExceptionReportApi } from '@/api/exception-reports';
import { ClientsConsideringOptionsRequestFilters } from '@/api/requests/exception-reports/clients-considering-options-request-filters';
import { Forbidden } from '@/api/responses/forbidden';
import clientsConsideringOptionsTable from '@/components/exception-reports/clients-considering-options-table.vue';
import loading from '@/components/loading.vue';
import { ClientsConsideringOptions } from '@/shared/exception-reports/clients-considering-options';
import { PartnerPractice } from '@/shared/partner-practice';
import { AppointmentNoInstructionType } from '@/shared/types/appointment-no-instruction-type';
import { AppointmentProductType } from '@/shared/types/appointment-product-type';
import { Component, Vue, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';

const partnerPracticesModule = namespace('partnerPractices');
const typesModule = namespace('types');
const exceptionReportApi = new ExceptionReportApi();

@Component({
    components: {
        clientsConsideringOptionsTable,
        loading,
    },
})
export default class ClientsConsideringOptionsExceptionReport extends Vue {
    @partnerPracticesModule.Getter('partnerPracticesForCurrentUser') private partnerPractices!: Array<PartnerPractice>;
    @typesModule.Getter('activeAppointmentProductTypes') private appointmentProductTypes!: Array<AppointmentProductType>;
    @typesModule.Getter('activeAppointmentNoInstructionTypes') private appointmentNoInstructionTypes!: Array<AppointmentNoInstructionType>;

    private isLoading: boolean = true;
    private appointmentDatePeriodFilter: number | null = null;
    private appointmentDateFromFilter: string | Date | null = null;
    private appointmentDateToFilter: string | Date | null = null;
    private partnerPracticeFilter: string | null = null;
    private legalAdvisorFilter: string | null = null;
    private accountNoFilter: number | null = null;
    private clientFirstNameFilter: string | null = null;
    private clientLastNameFilter: string | null = null;
    private appointmentProductTypeFilter: string | null = null;
    private instructionValueFilter: number | null = null;
    private noInstructionTypeFilter: string | null = null;
    private noInstructionNotesFilter: string | null = null;
    private appointments: Array<ClientsConsideringOptions> = new Array<ClientsConsideringOptions>();
    private reportName: string = 'Clients Considering Options';
    private emptyText: string | null = `Please enter some search filters and click Filter to show ${this.reportName}`;

    private datePeriods = new Array<{ id: number, value: string }>(...[
        { id: 0, value: 'Current Day' },
        { id: 1, value: 'Current Week' },
        { id: 2, value: 'Previous Week' },
        { id: 3, value: 'Current Month' },
        { id: 4, value: 'Previous Month' },
        { id: 5, value: 'Current Quarter' },
        { id: 6, value: 'Previous Quarter' },
        { id: 7, value: 'Current Year' },
        { id: 8, value: 'Previous Year' },
        { id: 9, value: 'Custom Dates' },
    ]);


    private get isCustomDatePeriod(): boolean {
        return this.appointmentDatePeriodFilter === 9;
    }

    private get requestFilters(): ClientsConsideringOptionsRequestFilters {
        return {
            appointmentDatePeriod: this.appointmentDatePeriodFilter,
            appointmentDateFrom: this.appointmentDateFromFilter,
            appointmentDateTo: this.appointmentDateToFilter,
            partnerPracticeId: this.partnerPractices.length === 1 ? this.partnerPractices[0].id! : this.partnerPracticeFilter,
            legalAdvisorId: this.legalAdvisorFilter,
            accountNo: this.accountNoFilter,
            clientFirstName: this.clientFirstNameFilter,
            clientLastName: this.clientLastNameFilter,
            appointmentProductTypeId: this.appointmentProductTypeFilter,
            instructionValue: this.instructionValueFilter,
            noInstructionTypeId: this.noInstructionTypeFilter,
            noInstructionNotes: this.noInstructionNotesFilter,
        };
    }

    @Watch('appointmentDatePeriodFilter')
    private async onAppointmentDatePeriodFilterChanged(): Promise<void> {
        this.appointmentDateFromFilter = null;
        this.appointmentDateToFilter = null;
    }

    public async getReportDataAsync(): Promise<void> {
        if (!(await this.$validator.validateAll())) {
            return;
        }

        this.isLoading = true;

        try {
            this.appointments = await exceptionReportApi.getClientsConsideringOptionsAsync(this.requestFilters);
            this.emptyText = `No ${this.reportName} found matching the requested search filters`;
        } catch (e) {
            if (e instanceof Forbidden) {
                this.$router.push({ name: 'forbidden' });
            } else {
                this.$snackbar.open({ message: `Could not load ${this.reportName}.`, type: 'is-danger' });
            }
        }

        this.isLoading = false;
    }

    private async exportReportAsync(): Promise<void> {
        if (!(await this.$validator.validateAll())) {
            return;
        }

        this.isLoading = true;

        try {
            const response = await exceptionReportApi.getClientsConsideringOptionsExportAsync(this.requestFilters);

            if (response == null) {
                this.$snackbar.open({ message: `No ${this.reportName} found matching the requested search filters.`, type: 'is-danger' });
                return;
            }

            const blob = new Blob([response], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const link = document.createElement('a');
            link.rel = 'noopener';
            link.href = window.URL.createObjectURL(blob);
            link.download = 'clients-considering-options.xlsx';
            link.target = '_blank';
            link.click();
            window.URL.revokeObjectURL(link.href);
        } catch (e) {
            if (e instanceof Forbidden) {
                this.$router.push({ name: 'forbidden' });
            } else {
                this.$snackbar.open({ message: `Could not download ${this.reportName}, please try again.`, type: 'is-danger' });
            }
        }

        this.isLoading = false;
    }

    private clearFilters(): void {
        this.appointmentDatePeriodFilter = null;
        this.appointmentDateFromFilter = null;
        this.appointmentDateToFilter = null;
        this.partnerPracticeFilter = null;
        this.legalAdvisorFilter = null;
        this.accountNoFilter = null;
        this.clientFirstNameFilter = null;
        this.clientLastNameFilter = null;
        this.appointmentProductTypeFilter = null;
        this.instructionValueFilter = null;
        this.noInstructionTypeFilter = null;
        this.noInstructionNotesFilter = null;
    }

    private async mounted(): Promise<void> {
        this.isLoading = true;

        await Promise.all([
            this.$store.dispatch(`partnerPractices/loadPartnerPracticesAsync`),
            this.$store.dispatch(`types/loadTypesAsync`),
            this.$store.dispatch(`types/loadPartnerPracticeTypesAsync`),
        ]);

        this.isLoading = false;
    }
}
